import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Tooltip,
  Card,
} from '@mui/material';
import {
  RecordVoiceOver as SpeakingIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { getSpeakingSessions, updateSpeakingSession, deleteSpeakingSession } from '../../api/adminApi';
import { useToast } from '../../contexts/ToastContext';
import ModernButton from '../../components/common/ModernButton';
import ModernDataGrid from '../../components/common/ModernDataGrid';
import ModernTextField from '../../components/common/ModernTextField';

const SpeakingSessionsManagement = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [completedFilter, setCompletedFilter] = useState('');
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(null);
  const { showToast } = useToast();

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (completedFilter !== '') params.isCompleted = completedFilter;

      const { data } = await getSpeakingSessions(params);
      setSessions(data.sessions);
      setTotalPages(data.pagination.pages);
    } catch (error) {
      console.error('Failed to fetch speaking sessions:', error);
      showToast('Failed to fetch speaking sessions', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, completedFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleEditSession = (session) => {
    setEditForm({
      _id: session._id,
      instructorFeedback: session.instructorFeedback || '',
      instructorAdvice: session.instructorAdvice || '',
      isCompleted: session.isCompleted,
    });
    setEditDialog(true);
  };

  const handleSaveSession = async () => {
    try {
      const { _id, ...updateData } = editForm;
      await updateSpeakingSession(_id, updateData);
      showToast('Session updated successfully', 'success');
      setEditDialog(null);
      fetchSessions();
    } catch (error) {
      showToast('Failed to update session', 'error');
    }
  };

  const handleDeleteSession = async () => {
    if (!deleteDialog) return;
    try {
      await deleteSpeakingSession(deleteDialog._id);
      showToast('Session deleted successfully', 'success');
      setDeleteDialog(null);
      fetchSessions();
    } catch (error) {
      showToast('Failed to delete session', 'error');
    }
  };

  const columns = [
    {
      field: 'user',
      headerName: 'User',
      flex: 1,
      renderCell: (params) => {
        const user = params.row.userProductRegistrationId?.userId;
        return user ? (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        ) : 'N/A';
      },
    },
    {
      field: 'startsAt',
      headerName: 'Session Time',
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {new Date(params.row.startsAt).toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Duration: {params.row.sessionDuration || 15} min
          </Typography>
        </Box>
      ),
    },
    {
      field: 'instructor',
      headerName: 'Instructor',
      width: 150,
      renderCell: (params) => {
        const instructor = params.row.instructorId;
        return instructor ? (
          <Typography variant="body2">
            {instructor.firstName} {instructor.lastName}
          </Typography>
        ) : 'N/A';
      },
    },
    {
      field: 'isOnlySpeaking',
      headerName: 'Type',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.row.isOnlySpeaking ? 'Only Speaking' : 'Exam Speaking'}
          color={params.row.isOnlySpeaking ? 'info' : 'primary'}
          size="small"
        />
      ),
    },
    {
      field: 'isReserved',
      headerName: 'Reserved',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.isReserved ? 'Yes' : 'No'}
          color={params.row.isReserved ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'isCompleted',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.isCompleted ? 'Completed' : 'Pending'}
          color={params.row.isCompleted ? 'success' : 'warning'}
          size="small"
        />
      ),
    },
    {
      field: 'isUserJoined',
      headerName: 'User',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.isUserJoined ? 'Joined' : 'Not Joined'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEditSession(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => setDeleteDialog(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ backgroundColor: 'info.main', width: 48, height: 48, mr: 2 }}>
            <SpeakingIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Speaking Sessions Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage all speaking sessions
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, p: 3, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Status"
              value={completedFilter}
              onChange={(e) => setCompletedFilter(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="true">Completed</MenuItem>
              <MenuItem value="false">Pending</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <ModernButton fullWidth variant="contained" onClick={fetchSessions}>
              Apply Filters
            </ModernButton>
          </Grid>
        </Grid>
      </Card>

      {/* Data Grid */}
      <ModernDataGrid
        rows={sessions}
        columns={columns}
        loading={loading}
        getRowId={(row) => row._id}
        pagination
        page={page}
        onPageChange={(newPage) => setPage(newPage + 1)}
        pageSize={20}
        rowCount={totalPages * 20}
        paginationMode="server"
      />

      {/* Edit Dialog */}
      {editDialog && (
        <Dialog open={editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Speaking Session</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <ModernTextField
                  fullWidth
                  label="Instructor Feedback"
                  multiline
                  rows={4}
                  value={editForm.instructorFeedback}
                  onChange={(e) => setEditForm({ ...editForm, instructorFeedback: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <ModernTextField
                  fullWidth
                  label="Instructor Advice"
                  multiline
                  rows={3}
                  value={editForm.instructorAdvice}
                  onChange={(e) => setEditForm({ ...editForm, instructorAdvice: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={editForm.isCompleted}
                  onChange={(e) => setEditForm({ ...editForm, isCompleted: e.target.value })}
                >
                  <MenuItem value={false}>Pending</MenuItem>
                  <MenuItem value={true}>Completed</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setEditDialog(null)}>Cancel</ModernButton>
            <ModernButton variant="contained" onClick={handleSaveSession}>
              Save Changes
            </ModernButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Dialog */}
      {deleteDialog && (
        <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
          <DialogTitle>Delete Speaking Session?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this speaking session?
            </Typography>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setDeleteDialog(null)}>Cancel</ModernButton>
            <ModernButton variant="contained" color="error" onClick={handleDeleteSession}>
              Delete
            </ModernButton>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default SpeakingSessionsManagement;
